import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  LinearProgress,
  useTheme,
} from '@mui/material';

const DAY_MS = 86400000;
const DEFAULT_CYCLE_DAYS = 42;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const CycleWeekIndicator = ({ cycle }) => {
  const theme = useTheme();

  if (!cycle || cycle.status !== 'active' || !cycle.startDate) {
    return null;
  }

  const start = new Date(`${cycle.startDate}T00:00:00`);

  let cycleDays = DEFAULT_CYCLE_DAYS;
  if (cycle.endDate) {
    const end = new Date(`${cycle.endDate}T00:00:00`);
    cycleDays = Math.max(1, Math.floor((end - start) / DAY_MS));
  }

  const totalWeeks = Math.ceil(cycleDays / 7);
  const daysElapsed = clamp(Math.floor((Date.now() - start) / DAY_MS), 0, cycleDays);
  const weekNumber = Math.min(totalWeeks, Math.floor(daysElapsed / 7) + 1);
  const percent = clamp((daysElapsed / cycleDays) * 100, 0, 100);
  const daysRemaining = Math.max(0, cycleDays - daysElapsed);

  // Internal week boundaries (skip 0% and 100% edges).
  const weekTicks = Array.from({ length: Math.max(0, totalWeeks - 1) }, (_, i) => ({
    left: ((i + 1) / totalWeeks) * 100,
    passed: percent >= ((i + 1) / totalWeeks) * 100,
  }));

  return (
    <Tooltip title={`${daysRemaining} days left`}>
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Typography
            variant="subtitle2"
            color="text.primary"
            sx={{ display: 'block', mb: 0.5, fontWeight: 700, letterSpacing: 0.3, textAlign: 'center' }}
          >
            Week {weekNumber} of {totalWeeks}
          </Typography>
          <Box sx={{ position: 'relative', width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              height: 14,
              borderRadius: 7,
              backgroundColor: '#2a2a2a',
              boxShadow: `inset 0 0 0 1px ${theme.palette.primary.dark}33`,
              '& .MuiLinearProgress-bar': {
                borderRadius: 7,
                transition: 'transform 0.8s ease-in-out',
                background: `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 0 8px ${theme.palette.primary.main}66`,
              },
            }}
          />
          {weekTicks.map((tick, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${tick.left}%`,
                width: '2px',
                transform: 'translateX(-1px)',
                borderRadius: 1,
                backgroundColor: tick.passed
                  ? `${theme.palette.primary.contrastText}55`
                  : '#1a1a1a',
              }}
            />
          ))}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default CycleWeekIndicator;
